import React, { Component } from 'react';
import debounce from 'lodash.debounce';

import './ReportContent.css';
import Label from '../label/Label';
import utils from '../../utils/utils';
import Volume from '../volume/Volume';
import models from '../../model/models';
import mainAPI from '../../api/MainAPI';
import ReportTypes from './reports.js';
import objUtils from '../../utils/objUtils';
import FontIcon from '../fontIcon/FontIcon';
import UniversalDropDown from '../universalDropDown/UniversalDropDown';
import { serialize } from '../../api/requests';
import FilterInput from '../../inputs/filterInput/FilterInput';
import { getDisplayUnit } from '../../utils/gramUtils';
import {List} from "react-virtualized";
import userGroupSettingsManager from "../../utils/UserGroupSettingsManager";

let thirtyDaysAgo = utils.xDaysAgo(30);
const noFilterReports = ['supply', 'expiring', 'inventory', 'narc-lock-lock-audit-report', 'fentanyl', 'custom'];

let lengths = {
	'1': 'one',
	'2': 'two',
	'3': 'three',
	'4': 'four',
	'5': 'five',
	'6': 'six',
	'7': 'seven',
	'8': 'eight',
	'9': 'nine',
	'10': 'ten',
	'11': 'eleven',
	'12': 'twelve',
	'13': 'thirteen',
	'14': 'fourteen',
	'15': 'fifteen'
};

let HoverOptions = (props) => {
	return (
		<div className="hover-options">
			<Label className="remove" gray-medium  onClick={() => props.removeColumn(props.name)}>
				Remove
			</Label>
			<FontIcon className="up" onClick={() => props.sortByColumn(props.name, 'desc')} name="icon-chevron-up" />
			<Label className="sort" gray-medium >
				Sort
			</Label>
			<FontIcon className="down" onClick={() => props.sortByColumn(props.name, 'asc')} name="icon-chevron-down" />
		</div>
	);
};

export default class ReportContent extends Component {
	constructor(props) {
		super(props);
		this.state = {
			data: [],
			formattedData: [],
			fields: this.reportMeta(this.props),
			columns: {
				type: { label: 'Type' },
				quantity: { label: 'Volume', type: 'volume' },
				lot_number: { label: 'Lot Number' },
				created_on: { label: 'Date', type: 'datetime' },
				expiration_date: { label: 'Expires', type: 'datetime' }
			}
		};
	}

	componentDidMount = async () => {
		const fields = this.reportMeta(this.props);
		const values = [
			{
				datetime: thirtyDaysAgo,
				operator: '>'
			}
		];
		const filterOptions = {};
		if (!noFilterReports.includes(this.props.reportType)) {
			const dateField = this.dateField() || 'created_on';
			filterOptions[dateField] = { values };
		}
		this.setState({ fields, filterOptions }, () => {
			this._getData(this.props);
			this._subscribe(this.props);
		});
		let [drug_types] = await Promise.all([
			mainAPI.drug_type.allowed_drugs(),
			userGroupSettingsManager.refresh()
		]);
		this.setState({ drug_types });

		this.handleResize = debounce(() => {
			this.setState({ lastResize: Date.now() });
		}, 300);
		window.addEventListener('resize', this.handleResize);
	};

	componentWillReceiveProps = (nextProps) => {
		let fields = this.reportMeta(nextProps);
		this.setState({ fields }, () => {
			this._getData(nextProps);
		});
	};

	componentWillUnmount = () => {
		if (this.subscribeId) {
			models[this.model(this.props)].unsubscribe(this.subscribeId);
		}
		if (this.handleResize) {
			window.removeEventListener('resize', this.handleResize);
		}
	};

	getFields = () => {
		return this.state.fields;
	};

	_subscribe = (props) => {
		if (this.subscribeId) return;
		if (!this.model(props)) return;
		let id = models[this.model(props)].onChange(() => {
			this._getData(this.props);
		});
		this.subscribeId = id;
	};

	_getData = async (props) => {
		if (this.browserRenderingNotAllowed(props)) return;
		if (this.hasData) return;
		let endpoint = this.endpoint(props);
		if (endpoint) this.hasData = true;
		let fieldOptions = await this._fieldOptions();
		let data = [];
		let model = this.model(props);
		this._subscribe(props);
		if (model) {
			data = models[model].objs();
			data = this._applyDateRange(data, props);
		} else if (endpoint) {
			data = await mainAPI[endpoint.table][endpoint.method](
				props.startDate,
				props.endDate,
				props.itemId,
				this.getSelectedFilterOptions()
			);
		}
		data = this._filter(data, props);
		data = this._sort(data, props);
		let formattedData = this._formatData(data, props, fieldOptions);
		this.setState({ data, fieldOptions, formattedData });
	};

	getSelectedFilterOptions = () => {
		let reportMeta = this.state.fields || {};
		const opts = {...reportMeta.endpointFilters ||{}, ...this.state.filterOptions || {}};
		if (this.state.fields) {
			if (this.state.fields.multiAgency) {
				opts.multiAgency = { multiAgency: true };
			}
		}
		return opts;
	};

	_formatData = (data, props, fieldOptions) => {
		let columnsWithAlterations = this.columns(props);
		let dataDisplay = [];
		let columns = fieldOptions;
		for (let i in data) {
			let dataRow = data[i];
			let row = {};
			for (let j in columns) {
				let column = columnsWithAlterations[j] || columns[j];
				row[j] = this._convertValue(dataRow[j], column, dataRow.type, j);
			}
			dataDisplay.push(row);
		}
		return dataDisplay;
	};

	_applyDateRange = (data, props) => {
		let dt = [];
		let dateField = this.dateField(props);
		for (let i in data) {
			let item = data[i];
			if (!item) continue;
			let date = item.id;
			if (dateField && item[dateField] && item[dateField].getTime) {
				date = item[dateField].getTime();
			}
			if ((!props.startDate || date > props.startDate) && (!props.endDate || date < props.endDate)) {
				dt.push(item);
			} else {
				// console.log('item removed from dataset', item);
			}
		}
		return dt;
	};

	_filter = (data, props) => {
		const filter = this.filter(props);
		if (!filter) return data;
		const {column, comparison, value: filterCriteria = ''} = filter;
		const filteredData = [];
		for (let i in data) {
			const dataRow = data[i];
			const dataValue = dataRow[column];
			if (comparison === '=' && dataValue == filterCriteria) {
				filteredData.push(dataRow);
			}
			if (comparison === '>' && dataValue > filterCriteria) {
				filteredData.push(dataRow);
			}
			if (comparison === '<' && dataValue < filterCriteria) {
				filteredData.push(dataRow);
			}
		}
		return filteredData;
	};

	_sort = (data) => {
		data = objUtils.to_array(data);
		if (!this.sort()) return data;
		let sortColumn = this.sortColumn();
		if (this.sortDirection() === 'desc') {
			data.sort((a, b) => {
				if (!b[sortColumn]) return -1;
				let val = 1;
				if (a[sortColumn] > b[sortColumn]) val = -1;
				if (a[sortColumn] == b[sortColumn]) val = 0;
				return val;
			});
		} else {
			data.sort((a, b) => {
				if (!b[sortColumn]) return 1;
				let val = 1;
				if (b[sortColumn] > a[sortColumn]) val = -1;
				if (a[sortColumn] == b[sortColumn]) val = 0;
				return val;
			});
		}
		return data;
	};

	dateField = (props) => {
		let reportMeta = this.state.fields;
		if (reportMeta.date_field) {
			return reportMeta.date_field;
		}
		return false;
	};

	filter = (props) => {
		const {fields: {filter, filter: {value = '', ...rest} = {}} = {}} = this.state || {};
		if (filter) {
			if (value.includes(':')) return {value: this.props.itemId, ...rest}
			return filter;
		}
		return false;
	};

	sort = (props) => {
		let reportMeta = this.state.fields;
		if (reportMeta.sort) return true;
		return false;
	};

	sortDirection = (props) => {
		let reportMeta = this.state.fields;
		return reportMeta.sort.direction;
	};

	sortColumn = (props) => {
		let reportMeta = this.state.fields;
		return reportMeta.sort.column;
	};

	endpoint = (props) => {
		let reportMeta = this.state.fields;
		return reportMeta.endpoint;
	};

	browserRenderingNotAllowed = () => {
    if(this.props.reportName === 'drug-checks') return true;
    let reportMeta = this.state.fields;
		return reportMeta.noBrowser;
	};

	getDataUrl = (props) => {
		const {reportName, itemId} = this.props
		let reportMeta = this.state.fields || {};
		if (!reportMeta.endpoint) {
			return null;
		}
		const userGroup = models.user_group.obj() || {};

		let data = {};
		data.token = localStorage.token;
		data.user_group_id = localStorage.user_group_id;
		data.last_log_in = localStorage.last_log_in;
		data.timezone_offset = new Date().getTimezoneOffset() / 60;
		data.timezone = userGroup.notification_timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
		data.filter = {...reportMeta.endpointFilters || {}, ...this.state.filterOptions || {}};
		if (this.state.fields.multiAgency) {
			data.filter.multiAgency = { multiAgency: true };
		}

		if (reportMeta.sort) {
			data.sort_by = reportMeta.sort.column;
			data.sort_direction = reportMeta.sort.direction;
		}

		if (reportName === 'unit') data.unit_id = itemId;
		const { downloadURI } = reportMeta
		if (downloadURI) {
			return `${window.location.origin}${downloadURI}?${serialize(data)}`
		}
		return (
			window.location.origin +
			'/api/v1/' +
			reportMeta.endpoint.table +
			'/' +
			reportMeta.endpoint.method +
			'?' +
			serialize(data)
		);
	};

	model = (props) => {
		let reportMeta = this.state.fields;
		return reportMeta.model;
	};

	columns = (props) => {
		let reportMeta = this.state.fields;
		return reportMeta.columns;
	};

	filterOptions = (props) => {
		const options = this.state.fields || {};
		if (!options) return null;
		const filterOptions = options.filterOptions;
		for (let i in filterOptions) {
			const filterOption = filterOptions[i];
			const {column, options} = filterOption;
			if (column === 'type' && this.state.drug_types) {
				filterOptions[i].options = this.state.drug_types;
			}
			if (options === ':build_set') {
				// Build a unique set of options from the existing data.
				const {formattedData = []} = this.state
				const values = new Set();
				// eslint-disable-next-line array-callback-return
				formattedData.map((row) => {
					if (row[column]) values.add(row[column])
				})
				const uniqueValues = Array.from(values).map((value) => ({id: value, value, name: value}));
				if (uniqueValues.length > 0) filterOptions[i].options = uniqueValues;
			}
		}
		return filterOptions;
	};

	reportMeta = (props) => {
		let reportType = props.reportType;
		if ((reportType === 'custom' && props.report) || (props.report && props.report.fields)) {
			return props.report.fields;
		}
		let report = ReportTypes[reportType] || {};
		return report;
	};

	_convertValue = (value, meta, drugType, name) => {
		try {
			let ret = value;
			if (meta.type == 'date') {
				if (!ret) ret = '';
				else ret = utils.display_actual_date(ret);
			}
			if (meta.type == 'datetime') {
				if (!ret) ret = '';
				else ret = utils.display_actual_datetime(ret);
			}
			if (meta.alteration) {
				ret = meta.alteration(ret);
				if (ret < 0) console.log(ret);
			}
			if (meta.type === 'volume') {
				ret = Volume.inPreferredUnit(ret, drugType) + ' ' + getDisplayUnit(drugType);
			}
			if (ret && meta.type === 'img') {
				ret = <img alt={'report img'} src={ret} />;
			}
			if (name === 'incident_number' && userGroupSettingsManager.getSetting('epcr_partner') === 'zoll') {
				ret = <a href={`https://zoll.emscharts.com/pr/summary_full.cfm?prid=${ret}`} target="_blank" rel="noopener noreferrer">{ret}</a>
			}

			if (ret && ret.replace) ret = ret.replace(/_/g, ' ');
			return ret;
		} catch (e) {
			console.error(e);
			return value;
		}
	};

	_length = (props) => {
		let columns = this.columns(props);
		let length = 0;
		// eslint-disable-next-line no-unused-vars
		for (let i in columns) {
			length++;
		}
		return lengths[length];
	};

	getExportData = () => {
		return {
			dataUrl: this.getDataUrl(),
			formattedData: this.state.formattedData,
			name: this.props.reportType,
			columns: this.columns(this.props)
		};
	};

	_removeColumn = (name) => {
		let fields = this.state.fields || {};
		let columns = fields.columns;
		delete columns[name];
		fields.columns = columns;
		this.setState({fields});
	};

	_addColumn = (value) => {
		let fieldOptions = this.state.fieldOptions || {};
		let fields = this.state.fields || {};
		let columns = fields.columns || {};
		columns[value] = fieldOptions[value];
		fields.columns = columns;
		this.setState({fields});
	};

	_sortByColumn = (column, direction) => {
		let fields = this.state.fields || {};
		fields.sort = { column, direction };
		let data = this.state.data || [];
		this.setState({ fields }, () => {
			data = this._sort(data, this.props);
			let formattedData = this._formatData(data, this.props, this.state.fieldOptions);
			this.setState({ formattedData, data });
		});
	};

	_fieldOptions = async () => {
		if (this.state.fieldOptions) return this.state.fieldOptions;
		let model = this.model();
		let endpoint = this.endpoint() || {};
		let endpoint_model = endpoint.table;
		let endpoint_method = endpoint.method;
		if (!model && !endpoint_model) return null;
		return mainAPI.report.fields({ model, endpoint_method, endpoint_model });
	};

	_formattedFieldOptions = () => {
		let options = {};
		let fieldOptions = this.state.fieldOptions;
		let columns = this.columns();
		for (let i in fieldOptions) {
			if (columns[i]) continue;
			options[i] = fieldOptions[i].label;
		}
		return options;
	};

	_totalRowTotal = () => {
		// eslint-disable-next-line no-undef,no-restricted-globals
		if(!this.state?.data?.length) return null;
		// eslint-disable-next-line no-restricted-globals
		return <Label className={'total'}>Total {this.state.data?.length}</Label>
	};

	_renderLabels = (props) => {
		let labelDisplay = [];
		let columns = this.columns(props);
		for (let i in columns) {
			labelDisplay.push(
				<Label className="removable-column" bold black key={i}>
					{columns[i].label}
					<HoverOptions name={i} sortByColumn={this._sortByColumn} removeColumn={this._removeColumn} />
				</Label>
			);
		}
		labelDisplay.push(this.renderFilter());
		labelDisplay.push(this._renderAddColumnButton());
		return <div className="row labels" key={"labels"}>{labelDisplay}</div>;
	};

	renderFilter = () => {
		const options = this.filterOptions();
		if (!options) return null;

		const default30DayFilter = !noFilterReports.includes(this.props.reportType) ? [
			{
				value: {
					datetime: thirtyDaysAgo,
					operator: '>'
				},
				name: utils.display_actual_datetime(thirtyDaysAgo),
				column: this.dateField() || 'created_on',
				displayName: 'Start'
			}
		] : null;

		return (<FilterInput currentFilter={this.state.rawFilterOptions || default30DayFilter} onChange={this.handleFilterChange} options={options}/>);
	};

	handleFilterChange = (filterOptions, rawFilterOptions) => {
		this.setState({ filterOptions,rawFilterOptions }, () => {
			this.hasData = false;
			this._getData(this.props);
		});
	};

	_renderAddColumnButton = () => {
		return (
			<UniversalDropDown
				placeholder="add column"
				onChange={this._addColumn}
				className="column-selector"
				options={this._formattedFieldOptions()}
			/>
		);
	};

	render() {
		const columns = this.columns(this.props);
		const {formattedData = [], data = []} = this.state;

		// Store the longest text in each row to calculate row height
		const maxLengthText = formattedData.map(row => {
			let value = "";
			Object.keys(columns).forEach((column) => {
				value = (typeof row[column] == 'string' && row[column].length > value.length) ? row[column] : value;
			})
			return value;
		})

		const renderRow = ({index, style, key}) => {
			const data = formattedData[index];
			const row = [];
			for (const j in columns) {
				let propertyValue = data[j];
				if (j === 'barcode_id') propertyValue = utils.upperCase(propertyValue);
				row.push(<Label black key={j}>{propertyValue}</Label>);
			}
			return (<div style={style} className="row" key={key}>{row}</div>)
		};

		const calculateRowHeight = ({ index }) => {
			const content = maxLengthText[index] || "";
			const lineHeight = 20; // Adjust this value as needed
			const padding = 15; // Adjust this value as needed
			// About 33 is an estimate of the characters fit on one line
			const charsPerLine = 23;
			const numberOfTextLines = Math.ceil(content.length / charsPerLine)
			const dynamicHeight = (numberOfTextLines * lineHeight) + padding;
			return dynamicHeight;
		};

		return (
			<div className={'g-report-content ' + this._length(this.props)}>
				{this._totalRowTotal()}
				{this._renderLabels(this.props)}
				<div className="content-rows">
					<List
						rowCount={data.length}
						width={window.innerWidth - 80}
						height={window.innerHeight - 312}
						rowHeight={calculateRowHeight}
						rowRenderer={renderRow}
						overscanRowCount={parseInt(Math.random() * 300)} // hack to force re-render after sort
					/>
				</div>
			</div>
		);
	}
}



// WEBPACK FOOTER //
// ./src/components/reportContent/ReportContent.jsx